import { ArrowPathIcon } from "@heroicons/react/20/solid";
import {
    activeTaskSort,
    ActiveTaskSortTypes,
    CalendarDate,
    completedTaskSort,
    CompletedTaskSortTypes,
    genEntityId,
    RecurrencePattern,
    Task,
    TaskId,
    TaskParentId,
    UserId,
} from "@sp-crm/core";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import { useListedActivityTypeName } from "components/tasks/activity-type-name";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { Checkbox } from "components/ui/checkbox";
import { PrimaryButton } from "components/ui/primary-button";
import { Select } from "components/ui/select";
import {
    TaskBasicsFragment,
    UpdateTaskPayload,
    useCreateTaskMutation,
    useDeleteTaskMutation,
    useGetPopulatedTasksForParentQuery,
    useUpdateTaskMutation,
} from "generated/graphql";
import * as React from "react";
import { useQueryClient } from "react-query";
import { refetchReminders } from "store/actions";
import {
    useCurrentUserId,
    usePreferences,
    useRegionId,
    useUsersForRegion,
} from "store/selectors/hooks";
import util from "../../../util";
import { breakNewlines } from "../../../util/text";
import { handleEvent } from "../../../util/user-events";
import { Header } from "../../header";
import { useCommitUserPreference } from "../../manage/account-settings/account-settings-hooks";
import { TaskFormV2 } from "../v2/task-form-v2";
import { Taskv2 } from "../v2/types";
import { EntityInfo, getEntityTypename } from "./types";

const futureDate = new Date(9999, 0, 1);

const taskWhen = (t: Taskv2): Date | null => Task.load(t.task).when();

const sortByDueDateAscending = (a: Taskv2, b: Taskv2): number => {
    const diff =
        (taskWhen(a) ?? futureDate).valueOf() - (taskWhen(b) ?? futureDate).valueOf();
    return diff === 0 ? a.task.id.localeCompare(b.task.id) : diff;
};

const sortByDueDateDescending = (a: Taskv2, b: Taskv2): number => {
    const diff =
        (taskWhen(b) ?? futureDate).valueOf() - (taskWhen(a) ?? futureDate).valueOf();
    return diff === 0 ? a.task.id.localeCompare(b.task.id) : diff;
};

const sortByCompletedDateAscending = (a: Taskv2, b: Taskv2): number => {
    const aDate = a.task.completedAt
        ? new Date(a.task.completedAt)
        : (taskWhen(a) ?? futureDate);
    const bDate = b.task.completedAt
        ? new Date(b.task.completedAt)
        : (taskWhen(b) ?? futureDate);
    const diff = aDate.valueOf() - bDate.valueOf();
    return diff === 0 ? a.task.id.localeCompare(b.task.id) : diff;
};

const sortByCompletedDateDescending = (a: Taskv2, b: Taskv2): number => {
    const aDate = a.task.completedAt
        ? new Date(a.task.completedAt)
        : (taskWhen(a) ?? futureDate);
    const bDate = b.task.completedAt
        ? new Date(b.task.completedAt)
        : (taskWhen(b) ?? futureDate);
    const diff = bDate.valueOf() - aDate.valueOf();
    return diff === 0 ? a.task.id.localeCompare(b.task.id) : diff;
};

const getActiveSorter = (
    sortOrder: ActiveTaskSortTypes,
): ((a: Taskv2, b: Taskv2) => number) => {
    switch (sortOrder) {
        case "dueDateDescending":
            return sortByDueDateDescending;
        case "dueDateAscending":
        default:
            return sortByDueDateAscending;
    }
};

const getCompletedSorter = (
    sortOrder: CompletedTaskSortTypes,
): ((a: Taskv2, b: Taskv2) => number) => {
    switch (sortOrder) {
        case "dueDateDescending":
            return sortByDueDateDescending;
        case "dueDateAscending":
            return sortByDueDateAscending;
        case "completedDateDescending":
            return sortByCompletedDateDescending;
        case "completedDateAscending":
        default:
            return sortByCompletedDateAscending;
    }
};

const activeTaskSortEntries = Object.entries(activeTaskSort).sort((a, b) =>
    a[1].localeCompare(b[1]),
);

const completedTaskSortEntries = Object.entries(completedTaskSort).sort((a, b) =>
    a[1].localeCompare(b[1]),
);

interface EntityTasksProps {
    parentId: TaskParentId;
    entityInfo: EntityInfo;
    defaultAssigneeId?: UserId;
}

export const EntityTasks: React.FC<EntityTasksProps> = props => {
    const { parentId, entityInfo, defaultAssigneeId } = props;
    const regionId = useRegionId();
    const currentUserId = useCurrentUserId();
    const defaultOwnerId = defaultAssigneeId ?? currentUserId ?? null;
    const queryClient = useQueryClient();
    const refreshAgenda = useAgendaRefresh();
    const [isCreating, setIsCreating] = React.useState(false);
    const [editingTaskId, setEditingTaskId] = React.useState<string | null>(null);
    const isFormDirtyRef = React.useRef(false);
    const onDirtyChange = React.useCallback((isDirty: boolean) => {
        isFormDirtyRef.current = isDirty;
    }, []);
    const confirmIfDirty = React.useCallback((): boolean => {
        if (isFormDirtyRef.current) {
            return window.confirm(
                "You have unsaved changes. Are you sure you want to discard them?",
            );
        }
        return true;
    }, []);

    const queryParams = React.useMemo(
        () => ({
            regionId,
            parentId,
        }),
        [regionId, parentId],
    );

    const tasksQuery = useGetPopulatedTasksForParentQuery(queryParams, {
        keepPreviousData: true,
    });

    const tasks: Taskv2[] = React.useMemo(
        () =>
            (tasksQuery.data?.getPopulatedTasksForParent ?? []).map(t => ({
                ...t,
                entity: {
                    ...t.entity,
                    contact: null as Taskv2["entity"]["contact"],
                },
            })),
        [tasksQuery.data?.getPopulatedTasksForParent],
    );

    const doRefetch = React.useCallback(async () => {
        await tasksQuery.refetch();
        refetchReminders(queryClient);
        await refreshAgenda();
    }, [tasksQuery, queryClient, refreshAgenda]);

    const preferences = usePreferences();
    const commitActiveSort = useCommitUserPreference("activeTaskSortOrder");
    const commitCompletedSort = useCommitUserPreference("completedTaskSortOrder");

    const activeTasks = React.useMemo(
        () =>
            tasks
                .filter(t => !t.task.done)
                .sort(getActiveSorter(preferences.activeTaskSortOrder)),
        [tasks, preferences.activeTaskSortOrder],
    );

    const completedTasks = React.useMemo(
        () =>
            tasks
                .filter(t => t.task.done)
                .sort(getCompletedSorter(preferences.completedTaskSortOrder)),
        [tasks, preferences.completedTaskSortOrder],
    );

    const createTask = useCreateTaskMutation();
    const updateTask = useUpdateTaskMutation();
    const deleteTask = useDeleteTaskMutation();

    const handleCreate = React.useCallback(
        async (_parentId: TaskParentId, payload: UpdateTaskPayload) => {
            await createTask.mutateAsync({ parentId, params: payload });
            handleEvent("task-create");
            setIsCreating(false);
            await doRefetch();
        },
        [parentId, createTask, doRefetch],
    );

    const handleUpdate = React.useCallback(
        async (taskId: TaskId, _parentId: TaskParentId, payload: UpdateTaskPayload) => {
            await updateTask.mutateAsync({ taskId, params: payload });
            setEditingTaskId(null);
            await doRefetch();
        },
        [updateTask, doRefetch],
    );

    const handleDelete = React.useCallback(
        async (taskId: TaskId) => {
            await deleteTask.mutateAsync({ taskId });
            await doRefetch();
        },
        [deleteTask, doRefetch],
    );

    const handleToggleComplete = React.useCallback(
        async (task: Taskv2, isComplete: boolean) => {
            if (isComplete) {
                handleEvent("task-complete", { source: "entity-tasks" });
            }
            await updateTask.mutateAsync({
                taskId: task.task.id,
                params: {
                    status: isComplete ? "done" : "new",
                    completedAt: isComplete ? new Date().toISOString() : null,
                    ownerId: task.task.ownerId,
                },
            });
            await doRefetch();
        },
        [updateTask, doRefetch],
    );

    const newTaskInitial: Taskv2 = React.useMemo(
        () => ({
            entity: {
                __typename: getEntityTypename(entityInfo.kind),
                displayName: entityInfo.displayName,
                path: entityInfo.path,
                kind: entityInfo.kind,
                contact: null as Taskv2["entity"]["contact"],
            },
            task: {
                __typename: "Task" as const,
                done: false,
                id: genEntityId(),
                parentId,
                status: "new",
                completedAt: null as TaskBasicsFragment["completedAt"],
                dueDate: CalendarDate.todayNoTime.toString(),
                dueDateTime: null as TaskBasicsFragment["dueDateTime"],
                ownerId: defaultOwnerId,
                taskTypeId: null as TaskBasicsFragment["taskTypeId"],
                taskType: null as TaskBasicsFragment["taskType"],
                text: "",
                notes: "",
                recurrencePattern: null as TaskBasicsFragment["recurrencePattern"],
                recurrenceInterval: null as TaskBasicsFragment["recurrenceInterval"],
                recurrenceDaysOfWeek: null as TaskBasicsFragment["recurrenceDaysOfWeek"],
                recurrenceDayOfMonth: null as TaskBasicsFragment["recurrenceDayOfMonth"],
            },
        }),
        [parentId, entityInfo, defaultOwnerId],
    );

    return (
        <div className="task-control w-full lg:w-1/2">
            {isCreating ? (
                <div className="tasks-section shadow bg-white">
                    <Header iconName="24_TaskNew">Create new task</Header>
                    <TaskFormV2
                        initial={newTaskInitial}
                        mode="create"
                        onClose={() => setIsCreating(false)}
                        saveTask={handleCreate}
                        entityLinkDisabled
                        onDirtyChange={onDirtyChange}
                    />
                </div>
            ) : (
                <div className="new-task-button">
                    <PrimaryButton
                        onClick={() => {
                            if (confirmIfDirty()) {
                                setEditingTaskId(null);
                                setIsCreating(true);
                            }
                        }}>
                        Create new task
                    </PrimaryButton>
                </div>
            )}
            <div className="bg-white rounded border border-gray-200 tasks-section active-tasks-section">
                <div className="task-section-header">
                    <div className="header">
                        <Header iconName="25_ActiveTasks">Active tasks</Header>
                    </div>
                    <div className="task-sort-by-picker">
                        <Select
                            label="Sort by"
                            value={preferences.activeTaskSortOrder}
                            onChange={e =>
                                commitActiveSort(e.target.value as ActiveTaskSortTypes)
                            }>
                            {activeTaskSortEntries.map(([value, text]) => (
                                <option key={value} value={value}>
                                    {text}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>
                <ul className="active-tasks tasks">
                    {activeTasks.map(t => (
                        <li key={t.task.id} className="show-tasks-list-item">
                            {editingTaskId === t.task.id ? (
                                <div className="p-2 lg:p-4">
                                    <TaskFormV2
                                        initial={t}
                                        mode="update"
                                        onClose={() => setEditingTaskId(null)}
                                        saveTask={async (pid, payload) => {
                                            await handleUpdate(t.task.id, pid, payload);
                                        }}
                                        entityLinkDisabled
                                        onDirtyChange={onDirtyChange}
                                    />
                                </div>
                            ) : (
                                <EntityTaskLineItem
                                    task={t}
                                    onToggleComplete={handleToggleComplete}
                                    onEdit={() => {
                                        if (confirmIfDirty()) {
                                            setIsCreating(false);
                                            setEditingTaskId(t.task.id);
                                        }
                                    }}
                                    onDelete={() => handleDelete(t.task.id)}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            {completedTasks.length > 0 && (
                <div className="tasks-section bg-white">
                    <div className="task-section-header">
                        <div className="header">
                            <Header iconName="23_TaskComplete">Completed tasks</Header>
                        </div>
                        <div className="task-sort-by-picker">
                            <Select
                                label="Sort by"
                                value={preferences.completedTaskSortOrder}
                                onChange={e =>
                                    commitCompletedSort(
                                        e.target.value as CompletedTaskSortTypes,
                                    )
                                }>
                                {completedTaskSortEntries.map(([value, text]) => (
                                    <option key={value} value={value}>
                                        {text}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    </div>
                    <ul className="tasks">
                        {completedTasks.map(t => (
                            <li key={t.task.id} className="show-tasks-list-item">
                                {editingTaskId === t.task.id ? (
                                    <div className="p-2 lg:p-4">
                                        <TaskFormV2
                                            initial={t}
                                            mode="update"
                                            onClose={() => setEditingTaskId(null)}
                                            saveTask={async (pid, payload) => {
                                                await handleUpdate(
                                                    t.task.id,
                                                    pid,
                                                    payload,
                                                );
                                            }}
                                            entityLinkDisabled
                                            onDirtyChange={onDirtyChange}
                                        />
                                    </div>
                                ) : (
                                    <EntityTaskLineItem
                                        task={t}
                                        onToggleComplete={handleToggleComplete}
                                        onEdit={() => {
                                            if (confirmIfDirty()) {
                                                setIsCreating(false);
                                                setEditingTaskId(t.task.id);
                                            }
                                        }}
                                        onDelete={() => handleDelete(t.task.id)}
                                    />
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

interface EntityTaskLineItemProps {
    task: Taskv2;
    onToggleComplete: (task: Taskv2, isComplete: boolean) => void;
    onEdit: () => void;
    onDelete: () => void;
}

const EntityTaskLineItem: React.FC<EntityTaskLineItemProps> = props => {
    const { task: taskv2, onToggleComplete, onEdit, onDelete } = props;
    const { task } = taskv2;
    const loaded = Task.load(task);
    const currentUserId = useCurrentUserId();
    const regionId = useRegionId();
    const users = useUsersForRegion(regionId);
    const owner = task.ownerId ? users?.[task.ownerId] : undefined;
    const taskTypeName = useListedActivityTypeName(task.taskType ?? null);
    const dueDate = task.dueDate
        ? CalendarDate.parse(task.dueDate).getOrElse(null)
        : null;

    return (
        <div className="show-task-control">
            <div className="task-mainline">
                <div className="task-text">
                    <Checkbox
                        onChange={e => onToggleComplete(taskv2, e.target.checked)}
                        checked={task.done}
                        label={
                            <div className="flex space-x-2 items-center">
                                <div>{task.text}</div>
                                {taskTypeName ? (
                                    <div className="bg-indigo-100 text-indigo-800 mr-3 inline-block flex-shrink-0 rounded-full px-3 py-0.5 text-sm font-medium">
                                        {taskTypeName}
                                    </div>
                                ) : null}
                            </div>
                        }
                    />
                    {task.notes ? (
                        <div className="task-notes">{breakNewlines(task.notes)}</div>
                    ) : null}
                </div>
                <div className="task-edit text-gray-500">
                    <EditButton onClick={onEdit} backgroundColor="bg-white" />
                    <DeleteButton
                        onClick={onDelete}
                        backgroundColor="bg-white"
                        confirm={{
                            title: "Delete task?",
                            message: `Really delete ${task.text}?`,
                        }}
                    />
                </div>
            </div>
            <div className="task-meta">
                <div className="assigned-to">
                    {owner ? (
                        <span>
                            Assigned to:{" "}
                            <strong className="semi-bold">
                                {util.user.displayName(owner)}
                                {owner.id === currentUserId ? <em>&nbsp;(me)</em> : null}
                            </strong>
                        </span>
                    ) : (
                        <em>unassigned</em>
                    )}
                </div>
                <div className="task-due">
                    {loaded.when() ? (
                        <span>
                            Due:{" "}
                            <strong className="semi-bold">
                                {dueDate
                                    ? util.date.formatCalendarDate(dueDate)
                                    : task.dueDateTime
                                      ? util.date.formattedDateTimeDefault(
                                            new Date(task.dueDateTime),
                                        )
                                      : null}
                            </strong>
                        </span>
                    ) : (
                        <span>No due date</span>
                    )}
                </div>
                {task.recurrencePattern &&
                    task.recurrencePattern !== RecurrencePattern.None && (
                        <div className="task-recurrence flex items-center space-x-1 text-sm text-gray-600">
                            <ArrowPathIcon className="w-4 h-4" />
                            <span>
                                {task.recurrencePattern === RecurrencePattern.Daily &&
                                    "Daily"}
                                {task.recurrencePattern === RecurrencePattern.Weekly &&
                                    "Weekly"}
                                {task.recurrencePattern === RecurrencePattern.Monthly &&
                                    "Monthly"}
                            </span>
                        </div>
                    )}
            </div>
            {task.done && task.completedAt && (
                <div className="task-meta-align-right">
                    <div className="task-due">
                        <span>
                            Completed:{" "}
                            <strong className="semi-bold">
                                {util.date.formatDateNoTime(new Date(task.completedAt))}
                            </strong>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export const useTaskBadge = (parentId: TaskParentId) => {
    const regionId = useRegionId();
    const queryParams = React.useMemo(
        () => ({ regionId, parentId }),
        [regionId, parentId],
    );
    const query = useGetPopulatedTasksForParentQuery(queryParams, {
        keepPreviousData: true,
    });
    const tasks = React.useMemo(
        () => query.data?.getPopulatedTasksForParent ?? [],
        [query.data?.getPopulatedTasksForParent],
    );
    const activeTasks = React.useMemo(() => tasks.filter(t => !t.task.done), [tasks]);
    const anyOverdue = React.useMemo(() => {
        const now = new Date();
        return activeTasks.some(t => {
            const loaded = Task.load(t.task);
            const when = loaded.when();
            return when && when < now;
        });
    }, [activeTasks]);
    return {
        activeCount: activeTasks.length || undefined,
        badgeType: anyOverdue ? ("error" as const) : ("info" as const),
    };
};

export const TaskBadge: React.FC<{ parentId: TaskParentId }> = ({ parentId }) => {
    const { activeCount, badgeType } = useTaskBadge(parentId);
    if (!activeCount) return null;
    return <Badge type={badgeType} value={activeCount} />;
};
